// A league's Home and its League page (HOME.md). Drawn through the kit
// (LOOKS.md Part 7).
//
// TWO SCREENS FROM ONE VIEW since B-211 and B-212. A league opens on My Team
// (the lineup), so Home is what a league shows before it has rosters — the
// draft countdown and the invite link — and before the draft the League tab's
// page is this one too, with a door to each screen a league has then. Once
// it has drafted, the League tab is the front page (B-403,
// `league/LeagueFront.tsx`).

import { formatInZone, racesOnPoints, whenInZone, type HomeView } from '@fsp/domain';
import { useNavigate } from 'react-router-dom';
import { useAccountPrefs } from '../data/accountPrefs';
import {
  useAwards,
  useHome,
  useLeagueBase,
  useReaderZone,
  useMyTeamId,
  useTeams,
} from '../data/hooks';
import { useSim } from '../data/SimProvider';
import { useTradeActions } from '../data/trades';
import { useSession } from '../data/session';
import { SignUp } from '../demo/SignUp';
import { useDelight } from '../delight/useDelight';
import { useChatDrawer } from '../shell/chatDrawer';
import { PositionPill, Surface, TeamMark } from '../kit';
import { Icon } from '../kit/Icon';
import { leagueDoors } from '../shell/leagueScreens';
import { Panel } from '../kit/Panel';
import { useLeagueSource } from '../data/leagueSource';
import { Button, Ink, Line, Text, TextField, tell } from '../kit/ui';
import '../styles/screens/home.css';
import { useAtLeast } from '../data/detail';
import { DashboardScreen, type Widget } from '../templates/DashboardScreen';
import { LeagueFront } from './league/LeagueFront';
import { AwardsWidget } from './shared/AwardsWidget';
import { whenLabel } from './NewsScreen';
import '../styles/screens/news.css';
import { HealthBar } from './shared/HealthBar';
import { MatchupCard } from './shared/MatchupCard';
import { FieldWeekCard } from './gameDay/FieldTable';
import { StatsNote } from './shared/StatsNote';
import { TwoGamesNote } from './shared/TwoGames';
import { FromLeague } from './shared/FromLeague';
import { PlayerName, TeamName } from './shared/Names';
import { Term } from '../kit/Why';
import { GLOSSARY } from './shared/glossary';

/**
 * Split for E12, like Schedule and Scoreboard: the body derives a dozen values
 * from the view and calls hooks keyed on them, so it cannot run before there
 * is one.
 */
export function HomeScreen() {
  return (
    <FromLeague snapshot={useHome()}>{(home) => <HomeBody home={home} page="home" />}</FromLeague>
  );
}

/**
 * The League tab's page: before the draft, the doors to the screens a league
 * has then (B-212); after it, the front page (B-403).
 */
export function LeagueScreen() {
  return (
    <FromLeague snapshot={useHome()}>
      {(home) =>
        home.league.status === 'setup' ? (
          <HomeBody home={home} page="league" />
        ) : (
          <LeagueFront home={home} />
        )
      }
    </FromLeague>
  );
}

/** Home sets no trade block; the actions need a list to diff against. */
const NO_BLOCK: readonly { playerId: string; wants: string }[] = [];

function HomeBody({ home, page }: { home: HomeView; page: 'home' | 'league' }) {
  const league = page === 'league';
  const myTeamId = useMyTeamId();
  const base = useLeagueBase();
  const teams = useTeams();
  // An offer's Decline goes where the league lives: the simulator or the API.
  const tradeActions = useTradeActions(home.league.id, myTeamId, NO_BLOCK);
  const chat = useChatDrawer();
  const navigate = useNavigate();
  const zone = useReaderZone();
  const setup = home.league.status === 'setup';
  const awards = useAwards();
  // Simple keeps Home to the screen's question: how am I doing, and what needs
  // me. The rest are summaries whose own screens are still one tap away in the
  // rail — nothing on Home is the only route to anything (DETAIL_LEVELS.md).
  // Setup is not leveled: those panels are the tasks, not a digest of them.
  const context = useAtLeast('standard') || setup;
  // A race on points has no record to put beside a team (B-361).
  const race = racesOnPoints(home.league.format.id);
  // The week my team went out of an elimination league (Q1), from my row.
  const myOut = home.standingsWindow.find((r) => r.team.isMine)?.eliminatedPeriod ?? null;
  // The install offer and arranging the page are the front page's now
  // (B-403), which is the League page once a league has drafted.
  const { account } = useAccountPrefs();
  // A MUTED READER'S PULSE HAS NO BOT IN IT (B-299), as their chat has none.
  const pulse =
    home.pulse === null || account?.chat?.hideBots !== true
      ? home.pulse
      : home.pulse.filter((p) => !(p.kind === 'chat' && p.bot === true));

  const widgets: Widget[] = [
    // Null health means nobody has checked the lineup (E1). The bar is the
    // one place this site speaks up unasked, so it must not speak up with
    // an "all good" it has not earned — it is absent instead.
    ...(home.health && !league
      ? [
          {
            id: 'health',
            pinned: true as const,
            content: <HealthBar health={home.health} linkToLineup={!setup} />,
          },
        ]
      : []),
    // THE DOORS, at every level: a level controls how many numbers, and
    // never hides a screen (DETAIL_LEVELS.md). First on the page, as HOME.md
    // has always said and arranging keeps (B-247); Your week and the install
    // offer came before them until 2026-10-02.
    ...(league
      ? [
          {
            id: 'doors',
            column: 1 as const,
            content: <LeagueDoors setup={setup} commissioner={home.league.isCommissioner} />,
          },
        ]
      : []),
    ...(home.myWeek && context
      ? [{ id: 'my-week', column: 1 as const, content: <MyWeekCard week={home.myWeek} /> }]
      : []),
    {
      id: 'matchup',
      column: 1,
      hidden: league,
      content: setup ? (
        <DraftHomeCard home={home} />
      ) : (
        <Panel title="This week">
          {home.matchup ? (
            <>
              {home.secondMatchup ? (
                // TWO OPPONENTS A WEEK (Q6, B-614): both games, the first
                // first, and what they are.
                <div className="fsp-two-games">
                  <MatchupCard m={home.matchup} awards={awards?.perPeriod} stats={home.stats} />
                  <MatchupCard
                    m={home.secondMatchup}
                    awards={awards?.perPeriod}
                    stats={home.stats}
                  />
                  <TwoGamesNote games={[home.matchup, home.secondMatchup]} />
                </div>
              ) : (
                <MatchupCard m={home.matchup} awards={awards?.perPeriod} stats={home.stats} />
              )}
              {/* When the numbers were last updated, so a 0 is not read
                      as a score (BACKLOG L2). */}
              <StatsNote stats={home.stats} spaced />
            </>
          ) : home.field && home.fieldOf !== null ? (
            // MY WEEK AGAINST THE FIELD (Q4, Q5, B-360, B-361), in place of
            // a matchup in a league with no opponents.
            <>
              <FieldWeekCard
                row={home.field}
                of={home.fieldOf}
                allPlay={home.league.format.id === 'all-play'}
              />
              <StatsNote stats={home.stats} spaced />
            </>
          ) : myOut !== null ? (
            // KNOCKED OUT (Q1, B-364): said plainly, not "no matchup".
            <div className="fsp-home-note">
              <Text tone="secondary">
                You went out after Week {myOut}. The rest of the league plays on.
              </Text>
            </div>
          ) : (
            <div className="fsp-home-note">
              <Text tone="secondary">No matchup this week.</Text>
            </div>
          )}
        </Panel>
      ),
    },
    {
      id: 'attention',
      column: 1,
      hidden: league,
      content: setup ? (
        <InviteWidget />
      ) : (
        <Panel title="Needs your attention">
          {home.attention === null ? (
            <Surface className="fsp-home-card">
              <Text tone="secondary">
                {/* Not "nothing pending": nobody has looked. The hub learned
                        this same lesson with `chores` and a screenshot. */}
                Not worked out yet.
              </Text>
            </Surface>
          ) : home.attention.filter((a) => a.kind !== 'draft').length === 0 ? (
            <Surface className="fsp-home-card">
              <Text tone="secondary">Nothing pending. Enjoy the week.</Text>
            </Surface>
          ) : null}
          {(home.attention ?? []).map((a) =>
            a.kind === 'trade-offer' ? (
              <Surface key={a.tradeId} className="fsp-home-card fsp-home-offer">
                <Line gap={3} align="start">
                  <TeamMark team={a.fromTeam} size={32} />
                  <div className="fsp-home-offer-body">
                    <Text weight="strong" block>
                      <TeamName team={a.fromTeam}>{a.fromTeam.ownerName}</TeamName> wants to trade
                    </Text>
                    <div className="fsp-sub fsp-ui-wrap-text">
                      {a.summary} · {hoursLeft(a.expiresAt, home.updatedAt)}h left
                    </div>
                  </div>
                </Line>
                <div className="fsp-home-offer-actions">
                  <Button
                    variant="primary"
                    full
                    onClick={() => navigate(`${base}/trades?trade=${a.tradeId}`)}
                  >
                    Look
                  </Button>
                  <Button full onClick={() => navigate(`${base}/trades?trade=${a.tradeId}`)}>
                    Counter
                  </Button>
                  <Button
                    full
                    variant="quiet"
                    onClick={() => {
                      void tradeActions
                        .answer(a.tradeId, 'decline', { drops: [], reason: null, veto: true })
                        .then((outcome) =>
                          tell(
                            outcome.ok ? 'Declined.' : outcome.message,
                            outcome.ok ? 'neutral' : 'bad',
                          ),
                        );
                    }}
                  >
                    Decline
                  </Button>
                </div>
              </Surface>
            ) : a.kind === 'waiver-pending' ? (
              <Surface
                key="waivers"
                className="fsp-row-clickable fsp-home-card fsp-home-claims"
                onClick={() => navigate(`${base}/waivers`)}
              >
                <div className="fsp-home-claims-body">
                  <Text weight="strong" block>
                    {a.count} waiver claim{a.count === 1 ? '' : 's'} pending
                  </Text>
                  <div className="fsp-sub">
                    {/* The reader's zone, or no time until it is known (B-181). */}
                    {[
                      zone === null ? null : `Processes ${fmtDay(a.processesAt, zone)}`,
                      a.budgetLeft !== null && a.budget !== null
                        ? `$${a.budgetLeft} of $${a.budget} left`
                        : null,
                    ]
                      .filter((part) => part !== null)
                      .join(' · ')}
                  </div>
                </div>
                <Button size="xs">Waivers</Button>
              </Surface>
            ) : null,
          )}
        </Panel>
      ),
    },
    // Nothing while a real league's awards are still being read: a chip
    // or a widget is never worth a spinner.
    ...(setup || !context || awards === null
      ? []
      : [
          {
            id: 'awards',
            column: 2 as const,
            content: <AwardsWidget awards={awards} />,
          },
        ]),
    {
      id: 'news',
      column: 2,
      hidden: !context,
      content: (
        <Panel title="Your players' news">
          <div>
            {home.news === null ? (
              <div className="fsp-home-note">
                <Text tone="secondary">
                  {/* Not the same sentence as "no news": the server has not
                          looked. Saying "quiet week" about something nobody read
                          would be inventing the quiet. */}
                  News is not available yet.
                </Text>
              </div>
            ) : home.news.length === 0 ? (
              <div className="fsp-home-note">
                <Text tone="secondary">
                  {setup
                    ? 'News starts once you have players.'
                    : 'Quiet week. No news on your roster.'}
                </Text>
              </div>
            ) : (
              home.news.map((n) => (
                <div key={n.id} className="fsp-row fsp-home-news">
                  <PositionPill pos={n.player.positions[0]!} size="sm" />
                  <span className="fsp-news-dot" data-severity={n.severity} aria-hidden />
                  <div className="fsp-home-news-body">
                    <div className="fsp-home-news-line">
                      <Text weight="strong">
                        <PlayerName player={n.player} club={n.player.proTeam}>
                          {n.player.shortName}
                        </PlayerName>
                      </Text>{' '}
                      {n.headline}
                    </div>
                    <div className="fsp-sub">
                      {zone === null ? null : whenLabel(n.publishedAt, zone)}
                    </div>
                  </div>
                </div>
              ))
            )}
            {!setup ? (
              <div className="fsp-home-links">
                <Button to={`${base}/news`} variant="quiet" size="xs">
                  All news
                </Button>
              </div>
            ) : null}
          </div>
        </Panel>
      ),
    },
    {
      id: 'pulse',
      column: 2,
      hidden: !context,
      content: (
        <Panel title="League pulse">
          <Surface className="fsp-row-clickable fsp-home-pulse" onClick={chat.toggle}>
            {pulse === null ? (
              <div className="fsp-home-note">
                <Text tone="secondary">
                  {/* An empty list and an uncomputed one look identical on a
                          screen, which is exactly why they must not read the
                          same. `?? []` here would say "nothing happened". */}
                  The league pulse is not available yet.
                </Text>
              </div>
            ) : pulse.length === 0 ? (
              <div className="fsp-home-note">
                <Text tone="secondary">
                  Nobody has said anything yet. Open chat and be the first.
                </Text>
              </div>
            ) : null}
            {(pulse ?? []).map((p) => (
              <div key={p.id} className="fsp-home-pulse-line">
                {p.kind === 'chat' ? (
                  <>
                    <Ink color={p.authorColor}>{p.author}</Ink> {p.body}
                  </>
                ) : p.kind === 'transaction' ? (
                  <span className="fsp-muted">{p.summary}</span>
                ) : (
                  <span>
                    <Text weight="bold">{p.title}</Text>
                    {p.detail ? <span className="fsp-muted"> · {p.detail}</span> : null}
                  </span>
                )}
              </div>
            ))}
          </Surface>
        </Panel>
      ),
    },
    {
      id: 'standings',
      column: 3,
      content: setup ? (
        <Panel title="Who's in">
          <div>
            {teams.map((t, i) => (
              <div
                key={t.id}
                className={`fsp-row fsp-home-seat ${t.isMine ? 'fsp-row-selected' : ''}`}
              >
                <span className="fsp-num fsp-muted">{i + 1}</span>
                {t.ownerName ? (
                  <TeamMark team={t} size={24} />
                ) : (
                  <span className="fsp-teammark fsp-teammark-open fsp-home-open-seat" />
                )}
                <div className="fsp-home-seat-body">
                  <div className="fsp-name">
                    <span className={`fsp-name-text ${t.isMine ? 'fsp-home-mine' : ''}`}>
                      {t.ownerName ? t.name : 'Open seat'}
                    </span>
                  </div>
                  <div className="fsp-sub">{t.ownerName || 'Invite someone'}</div>
                </div>
              </div>
            ))}
            <div className="fsp-home-links">
              <Button to={`${base}/rules`} variant="quiet" size="xs">
                League rules
              </Button>
            </div>
          </div>
        </Panel>
      ) : (
        <Panel title="Standings">
          <div>
            {/* WHAT THE NUMBERS ARE, and why the list starts where it does:
                the window is the teams around yours, and it read as a table
                missing its top with two unnamed columns (the page-by-page
                review of 2026-10-11). */}
            <div className="fsp-row fsp-home-standing fsp-home-standing-head" data-kbd-skip="">
              <span />
              <span className="fsp-cap">Around you</span>
              <span className="fsp-cap fsp-right">
                {/* A race on points has no record; how far back is its
                    column (B-361). */}
                {race ? (
                  <Term tip={GLOSSARY.pointsBack}>Back</Term>
                ) : (
                  <Term tip={GLOSSARY.winLoss}>W–L</Term>
                )}
              </span>
              <span className="fsp-cap fsp-right">
                <Term tip={GLOSSARY.pointsFor}>PF</Term>
              </span>
            </div>
            {home.standingsWindow.map((r) => (
              <button
                key={r.team.id}
                type="button"
                onClick={() => navigate(`${base}/teams/${r.team.id}`)}
                // Its name is its own words, rank to record (WCAG 2.5.3).
                title={`${r.team.name}'s roster`}
                className={`fsp-row fsp-row-clickable fsp-home-standing ${r.team.isMine ? 'fsp-row-selected' : ''}`}
              >
                <span className="fsp-num fsp-muted">{r.rank ?? '—'}</span>
                <span className={`fsp-home-standing-name ${r.team.isMine ? 'fsp-home-mine' : ''}`}>
                  {r.team.name}
                </span>
                <span className="fsp-num fsp-right">
                  {race
                    ? r.pointsBack === null || r.pointsBack === 0
                      ? '—'
                      : r.pointsBack.toFixed(1)
                    : `${r.wins}–${r.losses}${r.ties ? `–${r.ties}` : ''}`}
                </span>
                <span className="fsp-num fsp-right">{r.pointsFor.toFixed(1)}</span>
              </button>
            ))}
            <div className="fsp-home-links">
              <Button to={`${base}/standings`} variant="quiet" size="xs">
                Full standings
              </Button>
              <Button to={`${base}/rules`} variant="quiet" size="xs">
                League rules
              </Button>
            </div>
          </div>
        </Panel>
      ),
    },
  ];

  return <DashboardScreen widgets={widgets} />;
}

/**
 * Every league screen as a door (B-212): the phone's League tab used to be a
 * menu of these, and the rail lists most of them; here they are big enough to
 * find at a glance.
 */
function LeagueDoors({ setup, commissioner }: { setup: boolean; commissioner: boolean }) {
  const base = useLeagueBase();
  const navigate = useNavigate();
  const chat = useChatDrawer();
  const real = useLeagueSource() === 'api';
  return (
    <Panel title="Everything in the league">
      <nav className="fsp-league-doors" aria-label="League screens">
        {leagueDoors(base, setup, commissioner, real).map((d) => (
          <button
            key={d.label}
            type="button"
            className="fsp-menu-item"
            onClick={() => (d.kind === 'chat' ? chat.setOpen(true) : navigate(d.to))}
          >
            <Icon name={d.icon} size="lg" />
            <span>{d.label}</span>
          </button>
        ))}
      </nav>
    </Panel>
  );
}

/** "Your week in one sentence" (RECAP.md), from Tuesday until the next kickoff. */
function MyWeekCard({ week }: { week: NonNullable<HomeView['myWeek']> }) {
  const base = useLeagueBase();
  return (
    <Panel title="Your week" className="fsp-recap-mine" bodyClass="fsp-home-week">
      <div className="fsp-home-week-line">{week.line}</div>
      <div className="fsp-home-week-link">
        <Button to={`${base}/recap?period=${week.period}`} variant="quiet" size="xs">
          Read the recap
        </Button>
      </div>
    </Panel>
  );
}

/** The invite link and code (LEAGUE_SETUP.md): the thing a commissioner sends to the group chat. */
function InviteWidget() {
  const { state } = useSim();
  const { play } = useDelight();
  const { session } = useSession();
  if (!session)
    return (
      <Panel title="Invite link">
        <div className="fsp-home-invite">
          <Text block>Sign up to invite your friends. Everything you've done comes with you.</Text>
          <div>
            <SignUp size="sm" />
          </div>
        </div>
      </Panel>
    );
  const link = `${typeof location !== 'undefined' ? location.origin : ''}/join/${state.inviteCode}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      play('lock');
      tell('Copied. Send it to the group chat.', 'good');
    } catch {
      tell(`Copy this: ${link}`);
    }
  };
  const share = async () => {
    try {
      await navigator.share({
        title: state.leagueName,
        text: `Join ${state.leagueName}`,
        url: link,
      });
    } catch {
      // Dismissed or unsupported: the copy button is right there.
    }
  };
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  return (
    <Panel title="Invite link">
      <div className="fsp-home-invite">
        <Line gap={2} wrap>
          <span className="fsp-home-invite-link">
            <TextField label="Invite link" hideLabel value={link} onChange={() => undefined} />
          </span>
          <Button variant="primary" icon="copy" onClick={() => void copy()}>
            Copy
          </Button>
          {canShare ? (
            <Button icon="share" onClick={() => void share()}>
              Share
            </Button>
          ) : null}
        </Line>
        <div className="fsp-home-invite-code">
          <span className="fsp-invite-code">{state.inviteCode}</span>
          <span className="fsp-sub">or say the code out loud</span>
        </div>
      </div>
    </Panel>
  );
}

/** Pre-draft Home (HOME.md): the matchup region becomes the draft countdown. */
function DraftHomeCard({ home }: { home: HomeView }) {
  const { now } = useSim();
  const zone = useReaderZone();
  const navigate = useNavigate();
  const base = useLeagueBase();
  const draft = (home.attention ?? []).find((a) => a.kind === 'draft');
  const at = draft ? Date.parse(draft.startsAt) : null;
  const open = draft?.roomOpen ?? false;
  return (
    <Panel title="Draft" bodyClass="fsp-home-draft">
      <div className="fsp-disp fsp-home-draft-when">
        {at ? (zone === null ? '' : fmtDraft(at, zone)) : 'Not scheduled yet'}
      </div>
      <div className="fsp-sub fsp-ui-wrap-text">
        {open
          ? 'The room is open. Grab a seat.'
          : at
            ? `${countdown(at, now)}. ${home.league.teamCount} teams, snake, 90 seconds a pick.`
            : 'Ask your commissioner.'}
      </div>
      <Line gap={2} wrap>
        <Button variant={open ? 'primary' : 'secondary'} onClick={() => navigate(`${base}/draft`)}>
          {open ? 'Join draft room' : 'Open the draft room'}
        </Button>
        {open ? null : (
          // TODO(B5): a calendar export, which arrives with email.
          <Button variant="quiet" onClick={() => tell('Calendar export arrives with email.')}>
            Add to calendar
          </Button>
        )}
      </Line>
    </Panel>
  );
}

function hoursLeft(iso: string, nowIso: string): number {
  return Math.max(0, Math.round((Date.parse(iso) - Date.parse(nowIso)) / 3600000));
}

/** A run's time, read now, by the rule every waiver time follows (`whenInZone`). */
function fmtDay(iso: string, timeZone: string): string {
  return whenInZone(iso, timeZone, Date.now());
}

/**
 * The practice league's draft, in the reader's zone and named (B-181). Only a
 * practice league reaches this card: a real league in setup opens
 * `LeaguePreDraftScreen` on every path but its priorities (`App.tsx`).
 */
function fmtDraft(ms: number, zone: string): string {
  return formatInZone(ms, zone, { weekday: 'long', hour: 'numeric', minute: '2-digit' });
}

function countdown(at: number, now: number): string {
  const ms = at - now;
  if (ms <= 0) return 'Starting now';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 48) return `In ${Math.round(h / 24)} days`;
  if (h >= 1) return `In ${h}h ${m}m`;
  return `In ${m} minutes`;
}
